import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  FullscreenSpinner,
  Input,
  Spinner,
  useToast,
  type Column,
} from '@shared/ui';
import { carrierApi, type Carrier } from '@entities/carrier';

const baseShape = {
  name: z.string().min(1).max(128),
  contact: z.string().min(1).max(256),
  baseRate: z.coerce.number().nonnegative(),
  perKgRate: z.coerce.number().nonnegative(),
  perCbmRate: z.coerce.number().nonnegative(),
};

const createSchema = z.object({
  ...baseShape,
  country: z
    .string()
    .regex(/^[A-Z]{2}$/, 'ISO-3166 alpha-2, e.g. CN'),
});

const updateSchema = z.object(baseShape);

type CreateValues = z.infer<typeof createSchema>;
type UpdateValues = z.infer<typeof updateSchema>;

export function CarriersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Carrier | null>(null);
  const [toggling, setToggling] = useState<Carrier | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['carriers'],
    queryFn: () => carrierApi.list(false),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['carriers'] });

  const create = useMutation({
    mutationFn: (v: CreateValues) => carrierApi.create(v),
    onSuccess: () => {
      toast.success('Перевозчик создан');
      setCreateOpen(false);
      void invalidate();
    },
    onError: (e: Error) => toast.error('Не удалось создать', e.message),
  });

  const update = useMutation({
    mutationFn: (v: UpdateValues) => carrierApi.update(editing!.id, v),
    onSuccess: () => {
      toast.success('Сохранено');
      setEditing(null);
      void invalidate();
    },
    onError: (e: Error) => toast.error('Не удалось сохранить', e.message),
  });

  const toggle = useMutation({
    mutationFn: () =>
      toggling!.active ? carrierApi.deactivate(toggling!.id) : carrierApi.activate(toggling!.id),
    onSuccess: () => {
      toast.success(toggling!.active ? 'Деактивирован' : 'Активирован');
      setToggling(null);
      void invalidate();
    },
    onError: (e: Error) => toast.error('Ошибка', e.message),
  });

  const COLUMNS: Column<Carrier>[] = [
    { key: 'name', header: 'Name', cell: (r) => r.name },
    { key: 'country', header: 'Country', cell: (r) => r.country },
    { key: 'contact', header: 'Contact', cell: (r) => r.contact },
    { key: 'base', header: 'Base', cell: (r) => r.baseRate },
    { key: 'kg', header: 'Per kg', cell: (r) => r.perKgRate },
    { key: 'cbm', header: 'Per CBM', cell: (r) => r.perCbmRate },
    {
      key: 'active',
      header: 'Status',
      cell: (r) =>
        r.active ? <Badge variant="success">active</Badge> : <Badge variant="secondary">off</Badge>,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
            edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setToggling(r)}>
            {r.active ? 'off' : 'on'}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) return <FullscreenSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Перевозчики</h1>
        <Button onClick={() => setCreateOpen(true)}>+ Добавить</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <DataTable
            rows={data ?? []}
            columns={COLUMNS}
            rowKey={(r) => r.id}
            empty={<EmptyState title="Нет перевозчиков" />}
          />
        </CardContent>
      </Card>

      <CarrierCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        isPending={create.isPending}
        onSubmit={(v) => create.mutate(v)}
      />

      <CarrierEditDialog
        open={!!editing}
        carrier={editing}
        onOpenChange={(o) => !o && setEditing(null)}
        isPending={update.isPending}
        onSubmit={(v) => update.mutate(v)}
      />

      <ConfirmDialog
        open={!!toggling}
        onOpenChange={(o) => !o && setToggling(null)}
        title={toggling?.active ? 'Деактивировать перевозчика?' : 'Активировать перевозчика?'}
        description={
          toggling?.active
            ? 'Будет скрыт от vendor-приложений.'
            : 'Снова станет доступен в vendor-приложениях.'
        }
        confirmLabel={toggling?.active ? 'Деактивировать' : 'Активировать'}
        variant={toggling?.active ? 'destructive' : 'success'}
        isPending={toggle.isPending}
        onConfirm={() => toggle.mutate()}
      />
    </div>
  );
}

function CarrierCreateDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (v: CreateValues) => void;
  isPending: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      country: '',
      contact: '',
      baseRate: 0,
      perKgRate: 0,
      perCbmRate: 0,
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый перевозчик</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((v) => {
            onSubmit(v);
            reset();
          })}
          className="grid grid-cols-2 gap-3"
        >
          <FormField label="Name" htmlFor="name" error={errors.name?.message} className="col-span-2">
            <Input id="name" {...register('name')} />
          </FormField>
          <FormField label="Country (ISO-2)" htmlFor="country" error={errors.country?.message}>
            <Input id="country" maxLength={2} {...register('country')} />
          </FormField>
          <FormField label="Contact" htmlFor="contact" error={errors.contact?.message}>
            <Input id="contact" {...register('contact')} />
          </FormField>
          <FormField label="Base rate" htmlFor="baseRate" error={errors.baseRate?.message}>
            <Input id="baseRate" type="number" step="0.01" {...register('baseRate')} />
          </FormField>
          <FormField label="Per kg" htmlFor="perKgRate" error={errors.perKgRate?.message}>
            <Input id="perKgRate" type="number" step="0.01" {...register('perKgRate')} />
          </FormField>
          <FormField label="Per CBM" htmlFor="perCbmRate" error={errors.perCbmRate?.message}>
            <Input id="perCbmRate" type="number" step="0.01" {...register('perCbmRate')} />
          </FormField>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner /> : 'Создать'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CarrierEditDialog({
  open,
  carrier,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean;
  carrier: Carrier | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (v: UpdateValues) => void;
  isPending: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateValues>({
    resolver: zodResolver(updateSchema),
    values: carrier
      ? {
          name: carrier.name,
          contact: carrier.contact,
          baseRate: carrier.baseRate,
          perKgRate: carrier.perKgRate,
          perCbmRate: carrier.perCbmRate,
        }
      : undefined,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Изменить перевозчика</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((v) => onSubmit(v))} className="grid grid-cols-2 gap-3">
          <FormField label="Name" htmlFor="name" error={errors.name?.message} className="col-span-2">
            <Input id="name" {...register('name')} />
          </FormField>
          <FormField
            label="Country"
            htmlFor="country"
            className="col-span-2"
            hint="Изменение страны не поддерживается backend'ом."
          >
            <Input id="country" disabled value={carrier?.country ?? ''} />
          </FormField>
          <FormField label="Contact" htmlFor="contact" error={errors.contact?.message} className="col-span-2">
            <Input id="contact" {...register('contact')} />
          </FormField>
          <FormField label="Base rate" htmlFor="baseRate" error={errors.baseRate?.message}>
            <Input id="baseRate" type="number" step="0.01" {...register('baseRate')} />
          </FormField>
          <FormField label="Per kg" htmlFor="perKgRate" error={errors.perKgRate?.message}>
            <Input id="perKgRate" type="number" step="0.01" {...register('perKgRate')} />
          </FormField>
          <FormField label="Per CBM" htmlFor="perCbmRate" error={errors.perCbmRate?.message}>
            <Input id="perCbmRate" type="number" step="0.01" {...register('perCbmRate')} />
          </FormField>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Отмена
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Spinner /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
